import { Fragment, useEffect, useState } from 'react'
import { Alert, Button, Empty, Input, Spin } from 'antd'
import { CaretRightOutlined, SearchOutlined } from '@ant-design/icons'
import { useAuthSession } from '@/lib/session'
import { useCapacityData } from '../../hooks'
import type { CapacityPeriod, CapacitySettings, UtilisationStatus } from '../../types'
import { formatCapacityPeriodLabel, getCurrentRange, getPeriodUnit } from '../../utils'
import { productThemes } from '@/lib/productBranding'
import './CapacityTab.css'

interface Props {
  settings: CapacitySettings
}

const STATUS_LABEL: Record<UtilisationStatus, string> = {
  optimal: 'Optimal',
  'near-capacity': 'Near Capacity',
  'over-allocated': 'Over-allocated',
  'under-utilised': 'Under-utilised',
  'burnout-risk': 'Burnout Risk',
  unavailable: 'Unavailable',
}

export function CapacityTab({ settings }: Props) {
  const session = useAuthSession()
  const clientId = session?.currentClientId ?? ''
  const [range, setRange] = useState(() => getCurrentRange())
  const granularity = settings.defaultGranularity
  const allPeriodsLabel = `All ${getPeriodUnit(granularity)}s`
  const [teamFilter, setTeamFilter] = useState('All Teams')
  const [periodFilter, setPeriodFilter] = useState(allPeriodsLabel)
  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const [page, setPage] = useState(1)
  const pageSize = 20
  const dollarMode = settings.budgetMode === 'dollars'
  const metricLabel = dollarMode ? 'Amount' : 'Hours'
  const chartRequestKey = `${clientId}|${range.startDate}|${range.endDate}|${granularity}`
  const [loadedChartRequestKey, setLoadedChartRequestKey] = useState(chartRequestKey)

  const { data, isLoading, isFetching, isError, refetch } = useCapacityData(clientId, range.startDate, range.endDate, {
    team: teamFilter === 'All Teams' ? undefined : teamFilter,
    search: search.trim() || undefined,
    periodStart: periodFilter === allPeriodsLabel ? undefined : periodFilter,
    page,
    pageSize,
  })

  const teamOptions = ['All Teams', ...(data?.teamOptions ?? [])]
  const periodOptions = data?.chart ?? []
  const visiblePeriods = data?.chart ?? []
  const utilisationRows = data?.utilisation ?? []
  const chartLoading = isLoading || (isFetching && loadedChartRequestKey !== chartRequestKey)
  const teamLoading = isLoading || isFetching

  useEffect(() => {
    if (data && !isFetching) {
      setLoadedChartRequestKey(chartRequestKey)
    }
  }, [chartRequestKey, data, isFetching])

  if (isError && !data) {
    return (
      <Alert
        type="error"
        message="Failed to load capacity data"
        action={<Button size="small" onClick={() => {
          void refetch()
        }}>Retry</Button>}
      />
    )
  }

  const averageUtilisation = data?.kpis.avgUtilisationPct
  const chartMeta = buildChartMetaForMode(visiblePeriods, dollarMode)
  const totalStaff = data?.utilisationTotalCount ?? utilisationRows.length
  const currentPage = data?.utilisationPage ?? page
  const currentPageSize = data?.utilisationPageSize ?? pageSize
  const totalPages = Math.max(1, Math.ceil(totalStaff / Math.max(currentPageSize, 1)))

  function setStartDate(startDate: string) {
    if (!startDate) return
    setRange((current) => ({ ...current, startDate: startDate > current.endDate ? current.endDate : startDate }))
    setPeriodFilter(allPeriodsLabel)
    setPage(1)
  }

  function setEndDate(endDate: string) {
    if (!endDate) return
    setRange((current) => ({ ...current, startDate: current.startDate > endDate ? endDate : current.startDate, endDate }))
    setPeriodFilter(allPeriodsLabel)
    setPage(1)
  }

  function toggleRow(staffKey: string) {
    setExpandedRows((current) => ({ ...current, [staffKey]: !current[staffKey] }))
  }

  function handleSearch() {
    setSearch(inputValue)
    setPage(1)
  }

  return (
    <div className="capacity-wireframe">
      <div className="filter-bar">
        <span className="filter-bar-label">Period</span>
        <input type="date" className="filter-select" value={range.startDate} max={range.endDate} onChange={(event) => setStartDate(event.target.value)} />
        <span className="filter-connector">to</span>
        <input type="date" className="filter-select" value={range.endDate} min={range.startDate} onChange={(event) => setEndDate(event.target.value)} />
        <div className="filter-divider" />
        <span className="filter-bar-label">Team</span>
        <select
          className="filter-select"
          value={teamFilter}
          onChange={(event) => {
            setTeamFilter(event.target.value)
            setPage(1)
          }}
        >
          {teamOptions.map((team) => <option key={team}>{team}</option>)}
        </select>
      </div>

      <main className="capacity-main">
        <div className="section">
          <div className="section-header">
            <span className="section-title">Capacity vs. Demand</span>
            <div className="section-actions">
              {!dollarMode && (
                <span className="section-meta">
                  Utilization: <strong>{averageUtilisation == null ? '--' : `${Math.round(averageUtilisation)}%`}</strong> | Target: {settings.targetUtilisationPct}%
                </span>
              )}
            </div>
          </div>
          <div className="section-body-chart section-loading-container">
            {visiblePeriods.length > 0 ? (
              <>
                <div className="capacity-chart">
                  <div className="chart-y-axis">
                    {chartMeta.yAxisLabels.map((value) => (
                      <span key={value} className="chart-y-label">{value}</span>
                    ))}
                  </div>
                  <div className="chart-gridlines">
                    {chartMeta.gridLinePercents.map((percent) => (
                      <div key={percent} className="chart-gridline" style={{ top: `${percent}%` }} />
                    ))}
                  </div>
                  <div
                    className="capacity-line"
                    style={{ bottom: `calc(${chartMeta.capacityPercent / 100} * var(--capacity-chart-plot-height) + var(--capacity-chart-label-height))` }}
                  >
                    <span className="capacity-line-label">Capacity: {formatMetric(chartMeta.capacityMetric, dollarMode)} {dollarMode ? '' : granularityHrsLabel(granularity)}</span>
                  </div>
                  {visiblePeriods.map((period) => {
                    const key = `${period.weekStart}-${period.periodLabel}`
                    const metricValue = dollarMode ? period.committedAmount : period.committedHours
                    const barHeight = chartMeta.maxValue > 0 ? (metricValue / chartMeta.maxValue) * 180 : 0
                    const isOver = !dollarMode && period.utilisationPct >= 100
                    return (
                      <div key={key} className="capacity-bar-group">
                        <div className="bar-stack">
                          <div
                            className="bar-segment bar-tax"
                            style={{
                              height: `${Math.max(4, barHeight)}px`,
                              borderRadius: '4px 4px 0 0',
                              background: isOver ? '#dc2626' : productThemes.pracPlanner.primary,
                            }}
                          />
                        </div>
                        <span className="bar-label">{formatCapacityPeriodLabel(period, granularity)}</span>
                        <span className={`bar-hours-label ${isOver ? 'is-danger' : ''}`}>
                          {formatMetric(metricValue, dollarMode)}
                        </span>
                      </div>
                    )
                  })}
                </div>
                <div className="chart-legend">
                  <div className="legend-item"><div className="legend-dot" /> Committed {metricLabel}</div>
                  <div className="legend-item legend-divider"><span className="legend-capacity-line" /> Capacity Limit</div>
                </div>
              </>
            ) : chartLoading ? (
              <SectionLoading />
            ) : (
              <div className="section-empty">
                <Empty description="No capacity chart data found for this period" />
              </div>
            )}
            {visiblePeriods.length > 0 && chartLoading && <SectionLoadingOverlay />}
          </div>
        </div>

        <div className="section">
          <div className="section-header">
            <span className="section-title">Team Utilization</span>
            <div className="section-actions">
              <span className="filter-bar-label">{getPeriodUnit(granularity)}</span>
              <select
                className="filter-select"
                value={periodFilter}
                onChange={(event) => {
                  setPeriodFilter(event.target.value)
                  setPage(1)
                }}
              >
                <option value={allPeriodsLabel}>{allPeriodsLabel}</option>
                {periodOptions.map((period) => (
                  <option key={period.weekStart} value={period.periodStart ?? period.weekStart}>
                    {formatCapacityPeriodLabel(period, granularity)}
                  </option>
                ))}
              </select>
              <div className="filter-divider" />
              <Input
                placeholder="Search Staff..."
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onPressEnter={handleSearch}
                suffix={<SearchOutlined className="cursor-pointer text-brand-500" onClick={handleSearch} />}
                allowClear
                style={{ width: 180 }}
              />
            </div>
          </div>
          <div className="team-utilization-body section-loading-container">
            {utilisationRows.length > 0 ? (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Team</th>
                        {!dollarMode && <th>Available</th>}
                        <th>Committed</th>
                        <th>Utilization</th>
                        <th>Used</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {utilisationRows.map((staff) => {
                        const key = staff.staffId || staff.staffName
                        const expanded = !!expandedRows[key]
                        const statusClass = getUtilizationClass(staff.status)
                        const jobRows = staff.clients.flatMap((client) => (
                          client.jobs.map((job) => ({ client, job }))
                        ))
                        const hasDetails = jobRows.length > 0
                        return (
                          <Fragment key={key}>
                            <tr
                              className={hasDetails ? 'expand-toggle' : undefined}
                              onClick={hasDetails ? () => toggleRow(key) : undefined}
                            >
                              <td>
                                <strong className="staff-name">
                                  {hasDetails && (
                                    <CaretRightOutlined
                                      className={`capacity-expand-icon${expanded ? ' expanded' : ''}`}
                                      aria-hidden="true"
                                    />
                                  )}
                                  {staff.staffName}
                                </strong>
                              </td>
                              <td>{staff.team}</td>
                              {!dollarMode && <td>{formatAvailableMetric(staff.availableHours, false)}</td>}
                              <td>{formatMetric(dollarMode ? staff.committedAmount : staff.committedHours, dollarMode)}</td>
                              <td>
                                <div className="util-bar-wrap">
                                  <div className="util-bar-track">
                                    <div
                                      className={`util-bar-fill ${statusClass}`}
                                      style={{ width: `${staff.utilisationPct == null ? 0 : Math.min(staff.utilisationPct, 100)}%` }}
                                    />
                                  </div>
                                  <span className={`util-pct ${statusClass}`}>{formatUtilisation(staff.utilisationPct)}</span>
                                </div>
                              </td>
                              <td>{formatMetric(dollarMode ? staff.usedAmount : staff.usedHours, dollarMode)}</td>
                              <td>
                                <span className={`badge ${getBadgeClass(staff.status)}`}>
                                  {STATUS_LABEL[staff.status]}
                                </span>
                              </td>
                            </tr>
                            {hasDetails && expanded && jobRows.map(({ client, job }, index) => (
                              <tr key={`${key}-${job.jobId || job.jobName || index}`} className="expand-child visible">
                                <td colSpan={2}>{formatJobLabel(client.clientName, job.jobName)}</td>
                                {!dollarMode && <td>{formatAvailableMetric(job.committedHours, false)}</td>}
                                <td>{formatMetric(dollarMode ? job.committedAmount : job.committedHours, dollarMode)}</td>
                                <td>
                                  <div className="progress-bar-wrap">
                                    <div className="progress-track">
                                      <div className="progress-fill" style={{ width: `${job.pct == null ? 0 : Math.min(job.pct, 100)}%` }} />
                                    </div>
                                    <div className="progress-label">{formatUtilisation(job.pct)}</div>
                                  </div>
                                </td>
                                <td>{formatMetric(dollarMode ? job.usedAmount : job.usedHours, dollarMode)}</td>
                                <td />
                              </tr>
                            ))}
                          </Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="section-actions capacity-pagination">
                  <span className="section-meta">Page {currentPage} of {totalPages} | {totalStaff} staff</span>
                  <div className="filter-divider" />
                  <button type="button" className="btn btn-sm" disabled={currentPage <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Prev</button>
                  <button type="button" className="btn btn-sm" disabled={currentPage >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>Next</button>
                </div>
              </>
            ) : teamLoading ? (
              <SectionLoading />
            ) : (
              <div className="section-empty">
                <Empty description="No team utilization data found for this period" />
              </div>
            )}
            {utilisationRows.length > 0 && teamLoading && <SectionLoadingOverlay />}
          </div>
        </div>
      </main>
    </div>
  )
}

function SectionLoading() {
  return (
    <div className="section-loading-inline">
      <Spin size="large" />
    </div>
  )
}

function SectionLoadingOverlay() {
  return (
    <div className="section-loading-overlay">
      <Spin />
    </div>
  )
}

function granularityHrsLabel(g: string) {
  if (g === 'monthly') return 'hrs/mo'
  return 'hrs/wk'
}

function buildChartMetaForMode(periods: CapacityPeriod[], dollarMode: boolean) {
  const committedMax = Math.max(...periods.map((period) => dollarMode ? period.committedAmount : period.committedHours), 0)
  const availableValues = periods.map((period) => nonNegative(dollarMode ? period.committedAmount : period.availableHours))
  const totalAvailable = availableValues.reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0)
  const capacityMetric = Math.max(periods.length > 0 ? totalAvailable / periods.length : 0, 0)
  const maxValue = Math.max(committedMax, capacityMetric, 1)
  const axisTop = Math.ceil(maxValue / 100) * 100
  const yAxisLabels = [axisTop, axisTop * 0.8, axisTop * 0.6, axisTop * 0.4, axisTop * 0.2, 0]
    .map((value) => dollarMode ? Math.round(value) : Math.round(value))
  const capacityPercent = (capacityMetric / maxValue) * 100
  return {
    maxValue,
    capacityMetric,
    capacityPercent: Math.max(0, Math.min(100, capacityPercent)),
    yAxisLabels,
    gridLinePercents: [0, 20, 40, 60, 80],
  }
}

function formatHours(value: number) {
  if (!Number.isFinite(value)) return '0'
  return Number.isInteger(value) ? `${value}` : value.toFixed(1)
}

function formatMetric(value: number, dollarMode: boolean) {
  if (dollarMode) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0)
  }

  return `${formatHours(value)} hrs`
}

function formatAvailableMetric(value: number, dollarMode: boolean) {
  return formatMetric(nonNegative(value), dollarMode)
}

function formatUtilisation(value: number | null) {
  return value == null ? '--' : `${Math.round(value)}%`
}

function formatJobLabel(clientName: string, jobName: string) {
  return `${clientName || 'Unassigned client'} - ${jobName || 'Untitled job'}`
}

function nonNegative(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0
}

function getUtilizationClass(status: UtilisationStatus) {
  if (status === 'over-allocated' || status === 'burnout-risk') return 'red'
  if (status === 'near-capacity') return 'amber'
  if (status === 'unavailable') return ''
  return 'green'
}

function getBadgeClass(status: UtilisationStatus) {
  if (status === 'over-allocated' || status === 'burnout-risk') return 'badge-red'
  if (status === 'near-capacity') return 'badge-amber'
  if (status === 'under-utilised' || status === 'unavailable') return 'badge-blue'
  return 'badge-green'
}
